import React from 'react';
import * as CSS from 'csstype';
import styled, { css } from 'styled-components';
import { Input as ReakitInput } from 'reakit';
import { colors } from '../../settings';
import { Box } from '../Box';
import { FieldStyling, FieldFocusStyling, FieldPlaceholderStyling } from '../Field';
import { Prefix } from './Prefix';
import { Suffix } from './Suffix';

export interface BaseInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
    /**
     * When true, adds error styling
     */
    error?: boolean;
    /**
     * A non-interactive icon rendered in the left of the input
     */
    icon?: React.ComponentType<{ color: CSS.Property.Color | colors.Color }>;
    id: string;
    /**
     * Renders content in the right side of the input
     */
    suffix?: React.ReactNode;
    type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'date';
    /**
     * Removes the default browser minimum width, and allows the
     * component to wrap.
     * Used for MultiSelect
     */
    wrap?: boolean;
}

interface PrivateBaseInputProps extends BaseInputProps {
    /**
     * Renders content in the left side of the input
     * If rendering an icon, @see `icon`
     */
    prefix?: React.ReactNode;
}

const BaseInputContainer = styled(Box)<{
    disabled?: boolean;
    error?: boolean;
}>`
    ${FieldStyling}

    &:focus-within {
        ${FieldFocusStyling}
    }
`;

const BaseInputComponent = styled(ReakitInput)<{
    disabled?: BaseInputProps['disabled'];
    wrap?: BaseInputProps['wrap'];
}>`
    appearance: none;
    background-color: inherit;
    border: 0;
    border-radius: inherit;
    color: inherit;
    line-height: inherit;
    padding: 0;

    ${({ wrap }) =>
        wrap &&
        css`
            flex-grow: 1;
            min-width: 100px;
            width: 0;
        `}

    /* Disable Microsoft Edge default password reveal functionality */
    &::-ms-reveal,
    &::-ms-clear {
        display: none;
    }

    &:focus {
        outline: none;
    }

    &::placeholder {
        ${FieldPlaceholderStyling}
    }
`;

/**
 * A styled text input, which can be used to build speciality form components.
 * When used directly, ensure that an accessible label is present.
 *
 * For a more comprehensive component, @see Input.
 */
export const BaseInput = React.forwardRef<HTMLInputElement, PrivateBaseInputProps>(function (
    { disabled, error, icon, prefix, required, suffix, type = 'text', wrap, ...rest },
    ref
) {
    return (
        <BaseInputContainer
            display="flex"
            alignItems="center"
            background="bleachedAs"
            disabled={disabled}
            error={error}
        >
            <Box display="flex" flexGrow={1} flexWrap={wrap ? 'wrap' : undefined}>
                {(icon || prefix) && <Prefix icon={icon}>{prefix}</Prefix>}
                <BaseInputComponent
                    ref={ref}
                    type={type}
                    disabled={disabled}
                    required={required}
                    wrap={wrap}
                    aria-invalid={error}
                    {...rest}
                />
            </Box>
            {suffix && <Suffix>{suffix}</Suffix>}
        </BaseInputContainer>
    );
});
